<!--
   Component Work Detail
    A detailed view of a Work of Art
    @author Leander Eyer <leander.eyer@elead.ch>
-->

<template>
	<div  v-if="work != null" >

		<div :class="{ hidden: isMagnified}" class="work-detail"  @touchstart="$parent.touchStartSelect($event)" @touchend="$parent.touchEndSelect($event, index)">

			<!-- Controls -->
			<div class="work-detail-close" @click="alternativeIndex = 0; $parent.select()" v-bind:title="model.getDictionary('detail_close')">x</div>
			<div :class="{ invisible: work.getImages().isImage(this.alternativeIndex) == false}" class="work-detail-zoom" @click="isMagnified = true" v-bind:title="model.getDictionary('detail_zoom')">&#x26B2;</div>
			<div :class="{ invisible: index <= $parent.minIndex }" class="work-detail-prev" @click="alternativeIndex = 0; $parent.select(index - 1)" v-bind:title="model.getDictionary('detail_prev')">&#x25C0;</div>
			<div :class="{ invisible: index >= $parent.maxIndex }" class="work-detail-next" @click="alternativeIndex = 0; $parent.select(index + 1)" v-bind:title="model.getDictionary('detail_next')">&#x25B6;</div>

			<!-- Big Version of the Image -->
			<img 
				v-if="work.getImages().isImage(this.alternativeIndex)"  
				class='work-detail-main' 
				@click="isMagnified = true;" 
				:src="work.getImages().getImageURL('large', this.alternativeIndex)" />
			<div v-else 
				class='work-detail-main'
				v-html="work.getImages().getMultiMediaHTML(this.alternativeIndex)" ></div>

			<!-- Other Images of the same work -->
			<div v-if="work.getImages().getImageCount() > 1" class='work-alternatives'>
				<work-alternative
					v-for="(image, index) in work.getImages().getImages()"
					:key="index"
					:image="image"
					:index="index"
				></work-alternative>
			</div>

			<div v-if="work.getDescription() != ''" class='work-detail-description' v-html="work.getDescription()"></div>

			<!-- Detail Information -->
			<table class="work-detail-data">
				<tr v-if=" work.getTitle() != ''" class='work-detail-title'>
					<td> {{ model.getDictionary('detail_title') }}:</td>
					<td>{{ work.getTitle() }}</td>
				</tr>
				<tr v-if=" work.getOrigin() != ''">
					<td>{{ model.getDictionary('detail_origin') }}:</td>
					<td>{{ work.getOrigin() }}</td>
				</tr>
				<tr v-if=" work.getArtists().length > 0">
					<td>{{ model.getDictionary('detail_artist') }}:</td>
					<td>
						<span class='textLink'
							v-for="(artist, index) in work.getArtists()"
							:key="artist.getID()"
							@click="$root.$children [0].showCard('Artist', [artist.getURLTerm()])">
							<template v-if="index > 0">, </template>
							{{ artist.getFullName() }}
						</span>
					</td>
				</tr>

				<tr v-if=" work.getCollections().length > 0">
					<td>{{ model.getDictionary('detail_collection')  }}:</td>
					<td>
						<span
							v-for="(collection, index) in work.getCollections()"
							:key="collection.getID()">
							<template v-if="index > 0">, </template>
							<span v-if="collection.getID() != 0" class='textLink' @click="$root.$children [0].showCard('Collection', [collection.getURLTerm()])">{{ collection.getName() }}</span>
							<span v-else>{{ collection.getName() }}</span>
						</span>
					</td>
				</tr>
				<tr v-else-if=" work.getLocation() != ''">
					<td>{{ model.getDictionary('detail_location') }}:</td>
					<td>{{ work.getLocation() }}</td>
				</tr>

				<tr v-if=" work.getYear() != ''">
					<td>{{ model.getDictionary('detail_year') }}:</td>
					<td>{{ work.getYear() }}</td>
				</tr>
				<tr v-if=" work.getTechnique() != ''">
					<td>{{ model.getDictionary('detail_technique') }}:</td>
					<td>{{ work.getTechnique() }}</td>
				</tr>
				<tr v-if=" work.getSize() != ''">
					<td>{{ model.getDictionary('detail_size') }}:</td>
					<td>{{ work.getSize() }}</td>
				</tr>
				<tr v-if=" work.getCopyright() != ''">
					<td>Copyright:</td>
					<td>{{ work.getCopyright() }}</td>
				</tr>
			</table>

			<div class="work-detail-self" >
				<span v-if="work.getIsInSelection() == false" class="link" @click="addToSelection(work)">{{ model.getDictionary('detail_addselection') }}</span>
				<span v-else class="link" @click="removeFromSelection(work)">{{ model.getDictionary('detail_removeselection') }}</span>
			</div>

		</div>

		<work-full-screen v-if="isMagnified == true" :work="work" :alternativeIndex="alternativeIndex" ></work-full-screen>
	</div>
</template>

<script>

	import Vue from "vue";
	import { model } from "./../model/Model.js";
	import WorkFullScreen from "./WorkFullScreen.vue";

	// Subcomponent for works with several images
	const WorkAlternative = Vue.component("work-alternative", {
		props: ["image", "index"],
		template: `
	        <div class="work-alternative zoomable" :class="{ active: index == $parent.alternativeIndex}">
	            <img v-if='image.isImage()'
					class="link"
					:src="image.getImageURL('small')"
					@click="$parent.alternativeIndex = index;"
	            />
				<div v-else 
					class="link"
					v-html="image.getMultiMediaHTML('preview')" 
					@click="$parent.alternativeIndex = index;"></div>
	        </div>`
	});
	export default {

		props: ["list", "index"],

		components: { WorkAlternative, WorkFullScreen },

		data: function() { 
			return {
				// expose model for translations
				model: model,

				alternativeIndex: 0,
				isMagnified: false
			};
		},

		methods: {

			addToSelection: function(work) {
				model.getSelection().addWork(work);
			},

			removeFromSelection: function(work) {
				model.getSelection().removeWork(work);

				// if we are currently operating on the selection list, this element may become invisible
				if (this.list == model.getSelection().getList()) {
					if (this.list.length == 0) {
						this.$parent.cursor = -1;
					}
				}				
			}	
			
		},

		computed: {
			// The selected work
			work: function() {
				if (this.index < 0) {
					return  null;
				} else {
					return this.list [this.index];
				}
			}
		}

	};
</script>

<style>

@media (min-width: 1000px) {

	div.work-detail {
		width: 66rem;
	}

	div.work-detail-self {
		margin-top: 2rem;
	}

	div.work-detail-close, div.work-detail-prev, div.work-detail-next, div.work-detail-zoom {
		position: absolute;
		cursor: pointer;
	}

	div.work-detail-close {
		top: -0.25rem;
		right: -3rem;
		font-size: 2rem;
	}

	div.work-detail-zoom {
		top: 4rem;
		right: -3rem;
		font-size: 2rem;
		transform: rotate(45deg);
	}

	div.work-detail-prev {
		top: 0;
		left: -4rem;
		font-size: 1.75rem;
	}

	div.work-detail-next {
		top: 0;
		right: 0;
		font-size: 1.75rem;
	}

	div.work-detail-close:hover, div.work-detail-prev:hover, div.work-detail-next:hover, div.work-detail-zoom:hover {
		color: #333333;
	}

	img.work-detail-main {
		max-width: 62rem;
		max-height: 35rem;
		cursor: pointer;
	}

	div.work-detail-main {
		width: 62rem;
		height: 35rem;
		cursor: pointer;
	}

	div.work-alternatives {
		position: relative;
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		justify-content: flex-start;
		margin: 1rem 0 -1rem 0;
	}

	div.work-alternative {
		width: 5rem;
		height: 5rem;
		margin: 0 1rem 1rem 0;
		overflow: hidden;

		opacity: 0.5;
	}

	div.work-alternatives div.active, div.work-alternatives div.work-alternative:hover {
		opacity: 1 !important;
	}

	div.work-alternative img, div.work-alternative div {
		object-fit: cover;
		object-position: center;
		width: 5rem;
		height: 5rem;
	}

	div.work-detail-description {
		margin: 2rem 4rem 0 0;
	}

	table.work-detail-data {
		margin-top: 2rem;
	}

	table.work-detail-data td {
		padding: 0.25rem 3rem 0.25rem 0;
	}

	tr.work-detail-title {
		font-weight: bold;
	}

}

@media (max-width: 1000px) {

	div.work-detail {
		width: 100%;
		padding-bottom: 4rem;
	}

	div.work-detail-self {
		margin-top: 2rem;
	}

	div.work-detail-close, div.work-detail-prev, div.work-detail-next, div.work-detail-zoom {
		display: inline-block;
		cursor: pointer;
	}

	div.work-detail-close {
		padding: 1rem 2rem 1rem 0rem;
		font-size: 2rem;
	}

	div.work-detail-zoom {
		font-size: 2rem;
		transform: rotate(45deg);
		padding: 1rem 2rem 1rem 2rem;
	}

	div.work-detail-prev {
		font-size: 2rem;
		padding: 1rem 2rem 1rem 2rem;
	}

	div.work-detail-next {
		font-size: 2rem;
		padding: 1rem 2rem 1rem 2rem;
	}

	div.work-detail-close:hover, div.work-detail-prev:hover, div.work-detail-next:hover, div.work-detail-zoom:hover {
		color: #333333;
	}

	img.work-detail-main {
		max-width: 100%;
		max-height: 100%;
		cursor: pointer;
	}

	div.work-detail-main {
		max-width: 100%;
		max-height: 100%;
		cursor: pointer;
	}

	div.work-alternatives {
		position: relative;
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		justify-content: flex-start;
		margin: 1rem 0 -1rem 0;
	}

	div.work-alternative {
		width: 5rem;
		height: 5rem;
		margin: 0 1rem 1rem 0;
		overflow: hidden;

		opacity: 0.5;
	}

	div.work-alternatives div.active, div.work-alternatives div.work-alternative:hover {
		opacity: 1 !important;
	}

	div.work-alternative img, div.work-alternative div {
		object-fit: cover;
		object-position: center;
		width: 5rem;
		height: 5rem;
	}

	div.work-detail-description {
		margin: 2rem 4rem 0 0;
	}

	table.work-detail-data {
		margin-top: 2rem;
	}

	table.work-detail-data td {
		padding: 0.25rem 3rem 0.25rem 0;
	}

	tr.work-detail-title {
		font-weight: bold;
	}

}

</style>